import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { loginUser, registerUser, verifyOtp as verifyOtpService, resendOtp as resendOtpService } from '../services/authService';
import { setLogoutHandler } from '../utils/axiosSetup';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userPayload, setUserPayload] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to clear auth state
  const clearAuthState = useCallback(() => {
    localStorage.removeItem('eez7_client_token');
    localStorage.removeItem('eez7_client_isLoggedIn');
    localStorage.removeItem('eez7_client_userPayload');
    localStorage.removeItem('eez7_client_orgSlug');
    setUserPayload({});
    setIsLoggedIn(false);
  }, []);

  // Register logout handler with axios interceptor
  useEffect(() => {
    setLogoutHandler(clearAuthState);
    return () => setLogoutHandler(null);
  }, [clearAuthState]);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('eez7_client_token');
    const storedUser = localStorage.getItem('eez7_client_userPayload');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Check if user object has required fields
        if (parsedUser && (parsedUser.id || parsedUser.email)) {
          setUserPayload(parsedUser);
          setIsLoggedIn(true);
          console.log('User restored from localStorage:', parsedUser);
        } else {
          // Invalid user data, clear storage
          clearAuthState();
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        clearAuthState();
      }
    }
    setIsLoading(false);
  }, []);

  // Sync with localStorage whenever state changes
  useEffect(() => {
    if (isLoggedIn && userPayload.id) {
      localStorage.setItem('eez7_client_isLoggedIn', 'true');
      localStorage.setItem('eez7_client_userPayload', JSON.stringify(userPayload));
    }
  }, [isLoggedIn, userPayload]);

  const login = async (email, password, orgSlug) => {
    try {
      const data = await loginUser(email, password, orgSlug);
      console.log('Login response:', data);

      localStorage.setItem('eez7_client_token', data.token);
      if (orgSlug) localStorage.setItem('eez7_client_orgSlug', orgSlug);

      const userPayload = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        phoneNumber: data.user.phoneNumber
      };

      console.log('Setting user payload:', userPayload);
      setUserPayload(userPayload);
      setIsLoggedIn(true);

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const data = await registerUser(userData);
      // Now registration just sends OTP, doesn't log user in yet
      return data;
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (email, otp, password, orgSlug) => {
    try {
      const data = await verifyOtpService(email, otp, password, orgSlug);
      localStorage.setItem('eez7_client_token', data.token);

      const userPayload = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        phoneNumber: data.user.phoneNumber
      };

      setUserPayload(userPayload);
      setIsLoggedIn(true);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const resendOtp = async (email) => {
    try {
      const data = await resendOtpService(email);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    clearAuthState();
    console.log('User logged out');
  };

  const value = {
    isLoggedIn,
    setIsLoggedIn,
    user: userPayload, // Alias for backward compatibility
    userPayload,
    setUserPayload,
    login,
    register,
    verifyOtp,
    resendOtp,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
